export type RouterProvider = {
  id: string; name: string; kind: 'api'|'gateway'|'local'; base_url: string|null; enabled: boolean; priority: number;
  circuit_state: 'closed'|'open'|'half_open'; consecutive_failures: number; retry_after: string|null;
};
export type RouterModel = {
  id: number; provider_id: string; model_key: string; display_name: string; tier: 'economic'|'standard'|'premium';
  context_tokens: number; max_output_tokens: number; supports_tools: boolean; supports_vision: boolean;
  supports_reasoning: boolean; supports_structured_output: boolean; enabled: boolean;
};
export type RouterProfile = {
  id: 'economic'|'standard'|'advanced'|'critical'; name: string; description: string;
  primary_model_id: number|null; fallback_model_id: number|null; max_cost_micros: number;
  max_calls: number; requires_confirmation: boolean; enabled: boolean;
};
export type RouterTool = {
  code:string; name:string; description:string; category:string; executionMode:'read'|'write'|'template';
  requiresConfirmation:boolean; costMicros:number; enabled:boolean;
};
export type RouterCatalog = {
  revision: number; routerEnabled: boolean; updatedAt: string; updatedBy: string|null;
  providers: RouterProvider[]; models: RouterModel[]; profiles: RouterProfile[]; tools: RouterTool[];
  cache: { activeEntries:number; hits:number; globalEntries:number; companyEntries:number; branchEntries:number };
  knowledge: { totalChunks:number; eligibleChunks:number; staleChunks:number; systems:Array<{systemId:string; totalChunks:number; eligibleChunks:number; staleChunks:number}> };
  pilot: { enabledScopes:number; items:Array<{systemId:string; capability:string; companyId:string; branchId:string}> };
  cost: { reservedMicros:number; providerCostMicros:number; oktonCostMicros:number; customerChargeMicros:number; releasedMicros:number };
  metrics: {
    total:number; success:number; cacheHits:number; avoidedProviderCalls:number; providerCalls:number;
    providerAvoidancePercent:number; uncertainCalls:number; averageLatencyMs:number;
    providerCostMicros:number; oktonCostMicros:number; customerChargeMicros:number;
    routes:Array<{route:string; total:number}>;
    models:Array<{provider:string; model:string; total:number; providerCostMicros:number}>;
    systems:Array<{systemId:string; total:number; success:number}>;
  };
  learning: {
    total:number; approved:number; draft:number; globalApproved:number; usage:number;
    candidates:number; pendingCandidates:number; blockedCandidates:number;
    feedback:{ total:number; positive:number; negative:number; rephrased:number; actionSuccess:number; actionFailed:number };
    intents:{ total:number; active:number; intents:number; systems:number };
    evaluation:{ cases:number; activeCases:number; approvedForTraining:number; runs:number; passedRuns:number; averageScore:number; averageLatencyMs:number; providerCostMicros:number };
    quality:{ total:number; average:number; minimum:number; safe:number; needsReview:number };
  };
  policy:{
    canEnable:boolean;
    gates:{benchmarkComplete:boolean;benchmarkPassed:boolean;benchmarkFresh:boolean;intentQuality:boolean;providerHealthy:boolean;budgetAvailable:boolean;planAccountingReady:boolean};
    reasons:string[];
    benchmark:{activeCases:number;evaluatedCases:number;passedCases:number;minimumScore:number;averageScore:number;oldestLatestRun:string|null};
    quality:{total:number;safe:number;unsafe:number};
    providers:{enabled:number;healthy:number};
    budget:{availableMicros:number;halted:boolean};
  };
};
export type RouterDryRunInput = {
  text: string; requiresTools: boolean; requiresVision: boolean; requiresReasoning: boolean; critical: boolean; expectedOutputTokens: number;
};
export type RouterDryRunResult = {
  mode: 'dry-run'; routerEnabled: boolean; requestedProfile: string; profile: string; reasons: string[];
  inputTokensEstimate: number; outputTokensEstimate: number;
  primaryModel: null | { id:number; providerId:string; modelKey:string; displayName:string; tier:string };
  fallbackModel: null | { id:number; providerId:string; modelKey:string; displayName:string; tier:string };
  estimatedCost: null | { usdMicros:number; brlMicros:number };
  withinProfileBudget: boolean|null;
  budget: { limitMicros:number; availableMicros:number; consumedPercent:number; halted:boolean; action:string; withinGlobalBudget:boolean|null };
  tierZero?: { intent:string; confidence:number; matchedTerm:string; arguments:Record<string,unknown>; ready:boolean; tool:{ code:string; name:string; description:string; category:string; executionMode:string; requiresConfirmation:boolean; costMicros:number } };
  requiresConfirmation: boolean; wouldExecute: false;
};
export async function loadRouterCatalog(token:string,signal:AbortSignal):Promise<RouterCatalog>{
  const response=await fetch('https://keycloak.okton.com.br/platform-api/v1/admin/ai-router-catalog',{
    signal,credentials:'include',headers:{Authorization:'Bearer '+token}
  });
  if(!response.ok) throw new Error(response.status===403?'Sem acesso ao catálogo do Router.':'Não foi possível carregar o catálogo do Router.');
  const payload=await response.json();
  return payload.data as RouterCatalog;
}
export type ToolExecutionResult = {
  tool:string;
  context:{ companyId:string; branchId:string };
  data:Record<string,unknown>;
};
export async function dryRunRouter(token:string,signal:AbortSignal,input:RouterDryRunInput):Promise<RouterDryRunResult>{
  const response=await fetch('https://keycloak.okton.com.br/platform-api/v1/admin/ai-router-dry-run',{
    method:'POST',signal,credentials:'include',
    headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
    body:JSON.stringify(input)
  });
  if(!response.ok) throw new Error(response.status===422?'Solicitação inválida para simulação.':'Não foi possível executar a simulação do Router.');
  const payload=await response.json();
  return payload.data as RouterDryRunResult;
}
export type BenchmarkResult = {
  mode:'dry-run'; total:number; passed:number; failed:number; averageScore:number;
  results:Array<{caseId:string;name:string;score:number;passed:boolean;route:string;profile:string|null;model:string|null;latencyMs:number}>;
};
export async function runBenchmark(token:string,signal:AbortSignal):Promise<BenchmarkResult>{
  const response=await fetch('https://keycloak.okton.com.br/platform-api/v1/admin/ai-benchmark/dry-run',{
    method:'POST',signal,credentials:'include',
    headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
    body:JSON.stringify({limit:100})
  });
  if(!response.ok) throw new Error('Não foi possível executar o benchmark.');
  const payload=await response.json();
  return payload.data as BenchmarkResult;
}
export async function executeTool(token:string,signal:AbortSignal,tool:string,args:Record<string,unknown>):Promise<ToolExecutionResult>{
  const response=await fetch('https://keycloak.okton.com.br/platform-api/v1/admin/ai-tools/'+encodeURIComponent(tool),{
    method:'POST',signal,credentials:'include',
    headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
    body:JSON.stringify(args)
  });
  if(!response.ok){
    let message='Não foi possível executar a Tool.';
    try{const payload=await response.json(); if(typeof payload?.message==='string')message=payload.message;}catch{}
    throw new Error(message);
  }
  const payload=await response.json();
  return payload.data as ToolExecutionResult;
}

export type RouterResolveInput = RouterDryRunInput & {
  systemId:string;
  capability:string;
};
export type RouterResolveResult = {
  route:'tier0'|'cache'|'rag'|'llm'|'blocked';
  source:string;
  final:boolean;
  next?:'tool'|'provider'|'none';
  reason?:string;
  interactionId:string|null;
  confidence?:number;
  similarity?:number;
  answer?:string;
  response?:Record<string,unknown>;
  tool?:RouterDryRunResult['tierZero'];
  providerDecision:RouterDryRunResult|null;
  rag:Array<{id:number;path:string;title:string;sha256:string;score:number;content:string}>;
};
export async function resolveRouter(token:string,signal:AbortSignal,input:RouterResolveInput):Promise<RouterResolveResult>{
  const response=await fetch('https://keycloak.okton.com.br/platform-api/v1/ai/router/resolve',{
    method:'POST',signal,credentials:'include',
    headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
    body:JSON.stringify(input)
  });
  if(!response.ok){
    let message='Não foi possível resolver o pipeline.';
    try{const payload=await response.json();if(typeof payload?.message==='string')message=payload.message;}catch{}
    throw new Error(message);
  }
  const payload=await response.json();
  return payload.data as RouterResolveResult;
}
